import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft,
  Edit,
  Trash2,
  Download,
  FileText,
  Tag,
  Clock,
  User,
  Calendar,
  Share2,
  ChevronRight,
  AlertCircle,
} from "lucide-react";
import {
  AdminLayout,
  Card,
  Button,
  Badge,
  Modal,
  Breadcrumb,
} from "../components/AdminSharedComponents";
import api from "../../../../utils/api";
import ReactMarkdown from "react-markdown";

const ResearchDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [research, setResearch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Fetch research data
  useEffect(() => {
    const fetchResearch = async () => {
      setLoading(true);
      try {
        const response = await api.get(`/api/research/${id}`);
        setResearch(response.data);
        setError(null);
      } catch (err) {
        console.error("Error fetching research:", err);
        setError("Failed to load research details. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchResearch();
  }, [id]);

  // Handle delete research
  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      await api.delete(`/api/research/${id}`);
      setShowDeleteModal(false);
      navigate("/admin/researchs", {
        state: { notification: "Research project successfully deleted" },
      });
    } catch (err) {
      console.error("Error deleting research:", err);
      setError("Failed to delete research. Please try again.");
    } finally {
      setDeleteLoading(false);
    }
  };

  // Map area IDs to display names
  const areaNames = {
    minerals: "Mineral Research",
    cathodic: "Cathodic Protection",
    corrosion: "Corrosion Science",
  };

  // Status badge variants
  const getStatusVariant = (status) => {
    switch (status) {
      case "active":
        return "success";
      case "draft":
        return "default";
      case "archived":
        return "danger";
      default:
        return "default";
    }
  };

  // Area badge variants
  const getAreaVariant = (area) => {
    switch (area) {
      case "minerals":
        return "success";
      case "cathodic":
        return "primary";
      case "corrosion":
        return "warning";
      default:
        return "default";
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center min-h-screen">
          <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full"></div>
          <span className="ml-3">Loading research details...</span>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <div className="flex flex-col items-center justify-center min-h-screen">
          <div className="text-red-500 flex items-center mb-4">
            <AlertCircle size={24} className="mr-2" />
            <span>{error}</span>
          </div>
          <Button
            variant="primary"
            onClick={() => navigate("/admin/researchs")}
          >
            Return to Research List
          </Button>
        </div>
      </AdminLayout>
    );
  }

  if (!research) {
    return (
      <AdminLayout>
        <div className="flex flex-col items-center justify-center min-h-screen">
          <div className="text-gray-500 mb-4">Research not found</div>
          <Button
            variant="primary"
            onClick={() => navigate("/admin/researchs")}
          >
            Return to Research List
          </Button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {/* Breadcrumb Navigation */}
      <Breadcrumb
        items={[
          { label: "Research", path: "/admin/researchs" },
          { label: research.title, path: `/admin/researchs/detail/${id}` },
        ]}
        className="p-4 bg-white shadow-sm"
      />

      {/* Header with Actions */}
      <div className="flex items-center justify-between mb-6 bg-white p-4 shadow-sm">
        <div className="flex items-center">
          <Button
            variant="text"
            className="mr-4 p-2 hover:bg-gray-100 rounded-full transition-colors"
            onClick={() => navigate("/admin/researchs")}
            aria-label="Go back"
          >
            <ArrowLeft size={20} className="text-gray-600" />
          </Button>
          <h1 className="text-xl md:text-2xl font-bold text-gray-800 truncate max-w-2xl">
            {research.title}
          </h1>
        </div>
        <div className="flex items-center space-x-2">
          <Link to={`/admin/researchs/edit/${id}`}>
            <Button variant="outline" className="flex items-center">
              <Edit size={16} className="mr-2" />
              <span className="hidden sm:inline">Edit</span>
            </Button>
          </Link>
          <Button
            variant="danger"
            className="flex items-center"
            onClick={() => setShowDeleteModal(true)}
          >
            <Trash2 size={16} className="mr-2" />
            <span className="hidden sm:inline">Delete</span>
          </Button>
        </div>
      </div>

      <div className="px-4 md:px-6 pb-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Main content column */}
          <div className="md:col-span-2 space-y-6">
            {/* Featured Image */}
            {research.thumbnail_url && (
              <div className="bg-white rounded-lg shadow-md overflow-hidden">
                <img
                  src={`/uploads/research/${research.thumbnail_url}`}
                  alt={research.title}
                  className="w-full h-64 md:h-96 object-cover"
                  onError={(e) => {
                    console.error(
                      "Image failed to load:",
                      research.thumbnail_url
                    );
                    e.target.src = "/placeholder-image.jpg"; // Optional fallback
                  }}
                />
              </div>
            )}

            {/* Research Content */}
            <Card
              title="Research Content"
              accentColor="primary"
              className="shadow-md"
            >
              <div className="p-6 prose prose-blue max-w-none">
                <ReactMarkdown>{research.content}</ReactMarkdown>
              </div>
            </Card>

            {/* References */}
            {research.references && (
              <Card
                title="References"
                accentColor="primary"
                className="shadow-md"
              >
                <div className="p-6 space-y-4">
                  <div className="prose max-w-none">
                    {research.references.split("\n").map(
                      (reference, index) =>
                        reference.trim() && (
                          <p key={index} className="mb-2">
                            {index + 1}. {reference.trim()}
                          </p>
                        )
                    )}
                  </div>
                </div>
              </Card>
            )}

            {/* Additional Images */}
            {research.images && research.images.length > 0 && (
              <Card
                title="Research Images"
                accentColor="primary"
                className="shadow-md"
              >
                <div className="p-6">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {research.images.map((image, index) => (
                      <div
                        key={index}
                        className="aspect-square overflow-hidden rounded-lg"
                      >
                        <img
                          src={`/uploads/research/${image.url}`}
                          alt={`Research image ${index + 1}`}
                          className="w-full h-full object-cover hover:scale-105 transition-transform cursor-pointer"
                          onClick={() =>
                            window.open(
                              `/uploads/research/${image.url}`,
                              "_blank"
                            )
                          }
                          onError={(e) => {
                            console.error("Image failed to load:", image.url);
                            e.target.src = "/placeholder-image.jpg";
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Research Overview */}
            <Card
              title="Research Overview"
              accentColor="primary"
              className="shadow-md"
            >
              <div className="p-4 space-y-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">
                    Research Area
                  </h3>
                  <Badge
                    variant={getAreaVariant(research.area)}
                    className="text-sm"
                  >
                    {areaNames[research.area]}
                  </Badge>
                </div>

                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">
                    Status
                  </h3>
                  <Badge
                    variant={getStatusVariant(research.status)}
                    className="text-sm"
                  >
                    {research.status.charAt(0).toUpperCase() +
                      research.status.slice(1)}
                  </Badge>
                </div>

                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">
                    Description
                  </h3>
                  <p className="text-gray-700">{research.description}</p>
                </div>

                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">
                    Keywords
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {research.keywords && research.keywords.length > 0 ? (
                      research.keywords.map((keyword, index) => (
                        <Badge
                          key={index}
                          variant="default"
                          className="text-xs"
                        >
                          {keyword}
                        </Badge>
                      ))
                    ) : (
                      <span className="text-gray-400 text-sm italic">
                        No keywords
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </Card>

            {/* Author and Publication */}
            <Card
              title="Author & Publication Details"
              accentColor="primary"
              className="shadow-md"
            >
              <div className="p-4 space-y-4">
                <div className="flex items-center">
                  <User size={16} className="text-gray-400 mr-2" />
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">
                      Author
                    </h3>
                    <p className="text-gray-700">{research.author}</p>
                  </div>
                </div>

                <div className="flex items-center">
                  <Calendar size={16} className="text-gray-400 mr-2" />
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">
                      Created
                    </h3>
                    <p className="text-gray-700">
                      {formatDate(research.created_at)}
                    </p>
                  </div>
                </div>

                <div className="flex items-center">
                  <Clock size={16} className="text-gray-400 mr-2" />
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">
                      Last Updated
                    </h3>
                    <p className="text-gray-700">
                      {formatDate(research.updated_at)}
                    </p>
                  </div>
                </div>
              </div>
            </Card>

            {/* Actions */}
            <Card title="Actions" accentColor="primary" className="shadow-md">
              <div className="p-4 space-y-3">
                <Button
                  variant="outline"
                  className="w-full justify-start flex items-center"
                >
                  <Download size={16} className="mr-2" />
                  Export as PDF
                </Button>

                <Button
                  variant="outline"
                  className="w-full justify-start flex items-center"
                >
                  <Share2 size={16} className="mr-2" />
                  Share Research
                </Button>

                <Link to={`/admin/researchs/edit/${id}`}>
                  <Button
                    variant="outline"
                    className="w-full justify-start flex items-center"
                  >
                    <Edit size={16} className="mr-2" />
                    Edit Research
                  </Button>
                </Link>
              </div>
            </Card>

            {/* Related Research */}
            <Card
              title="Related Research"
              accentColor="primary"
              className="shadow-md"
            >
              <div className="p-4 divide-y">
                <div className="py-3">
                  <Link
                    to="#"
                    className="flex items-center justify-between group"
                  >
                    <div>
                      <h4 className="font-medium text-primary-dark group-hover:text-primary">
                        Similar research title in the same area
                      </h4>
                      <p className="text-sm text-gray-500">
                        Mineral Research • Draft
                      </p>
                    </div>
                    <ChevronRight size={16} className="text-gray-400" />
                  </Link>
                </div>
                <div className="py-3">
                  <Link
                    to="#"
                    className="flex items-center justify-between group"
                  >
                    <div>
                      <h4 className="font-medium text-primary-dark group-hover:text-primary">
                        Another related research project
                      </h4>
                      <p className="text-sm text-gray-500">
                        Corrosion Science • Active
                      </p>
                    </div>
                    <ChevronRight size={16} className="text-gray-400" />
                  </Link>
                </div>
                <Button
                  variant="text"
                  className="w-full justify-center flex items-center mt-2"
                >
                  View All Related Research
                </Button>
              </div>
            </Card>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Research"
        actions={
          <>
            <Button variant="text" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleDelete}
              disabled={deleteLoading}
            >
              {deleteLoading ? "Deleting..." : "Delete"}
            </Button>
          </>
        }
      >
        <div className="p-2">
          <p className="mb-4">
            Are you sure you want to delete the research "{research.title}"?
          </p>
          <div className="bg-yellow-50 p-3 rounded border border-yellow-200 flex items-start">
            <AlertCircle
              size={18}
              className="text-yellow-600 mr-2 mt-0.5 flex-shrink-0"
            />
            <p className="text-sm text-yellow-700">
              This action cannot be undone. All data associated with this
              research project will be permanently removed.
            </p>
          </div>
        </div>
      </Modal>
    </AdminLayout>
  );
};

export default ResearchDetail;
